import { CURRENCY_SYMBOL } from '../utils/currency';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { vendorApi } from '../services/api';
import type { ManualMemberFormData, RenewalFormData, CancellationFormData } from '../types/memberships';

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (data?.message) return data.message;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && first.length > 0) return first[0] as string;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

interface MutationCallbacks {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export function useMemberMutations(venueSlug: string | undefined, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  const addManualMember = useMutation({
    mutationFn: async (formData: ManualMemberFormData & { force?: boolean }) => {
      if (!venueSlug) throw new Error('Venue slug is required');
      const payload: Record<string, any> = {
        name: formData.name,
        package_id: formData.package_id,
        payment_method: formData.payment_method,
        start_date: formData.start_date || undefined,
        notes: formData.notes || undefined,
        force: formData.force || false,
      };
      if (formData.email) payload.email = formData.email;
      if (formData.phone) payload.phone = formData.phone;
      if (formData.price) payload.price = parseFloat(formData.price);
      return vendorApi.addManualMember(venueSlug, payload);
    },
    onSuccess: (data) => {
      // If backend returned a warning (duplicate membership), don't invalidate or show success
      // Parent component will handle showing the confirmation dialog
      if (data.status === 'warning') {
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['venue-members', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['earnings'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['customerInsights'] });
      queryClient.invalidateQueries({ queryKey: ['customerProfile'] });
      callbacks?.onSuccess?.('Member added successfully');
    },
    onError: (error: unknown) => {
      callbacks?.onError?.(extractErrorMessage(error, 'Failed to add member'));
    },
  });

  const sendReminder = useMutation({
    mutationFn: async (membershipId: number) => {
      if (!venueSlug) throw new Error('Venue slug is required');
      return vendorApi.sendMemberReminder(venueSlug, membershipId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-members', venueSlug] });
      callbacks?.onSuccess?.('Reminder email sent successfully');
    },
    onError: (error: unknown) => {
      callbacks?.onError?.(extractErrorMessage(error, 'Failed to send reminder'));
    },
  });

  const renewMembership = useMutation({
    mutationFn: async ({ userId, formData }: { userId: number; formData: RenewalFormData }) => {
      if (!venueSlug) throw new Error('Venue slug is required');
      if (!formData.package_id) throw new Error('Package is required');

      const payload = {
        package_id: formData.package_id as number,
        payment_method: formData.payment_method,
        price: formData.price ? parseFloat(formData.price) : undefined,
        start_date: formData.start_date || undefined,
        notes: formData.notes || undefined,
      };
      return vendorApi.renewMembership(venueSlug, userId, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['venue-members', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['member-detail', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['earnings'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['customerInsights'] });
      queryClient.invalidateQueries({ queryKey: ['customerProfile'] });

      const info = data.data?.renewal_info;
      const message = info?.was_extended
        ? `Membership renewed! New period: ${info.new_start_date} to ${info.new_end_date}`
        : 'Membership renewed successfully';
      callbacks?.onSuccess?.(message);
    },
    onError: (error: unknown) => {
      callbacks?.onError?.(extractErrorMessage(error, 'Failed to renew membership'));
    },
  });

  const cancelMembership = useMutation({
    mutationFn: async ({ membershipId, formData }: { membershipId: number; formData: CancellationFormData }) => {
      if (!venueSlug) throw new Error('Venue slug is required');
      if (!formData.reason.trim()) throw new Error('Cancellation reason is required');

      const payload = {
        reason: formData.reason.trim(),
        refund_type: formData.refund_type,
        refund_amount: formData.refund_amount,
      };
      return vendorApi.cancelMembership(venueSlug, membershipId, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['venue-members', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['member-detail', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
      queryClient.invalidateQueries({ queryKey: ['earnings'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['customerInsights'] });
      queryClient.invalidateQueries({ queryKey: ['customerProfile'] });

      const refundAmount = data.data?.refund_amount;
      const message = refundAmount && refundAmount > 0
        ? `Membership cancelled. Refund of ${CURRENCY_SYMBOL}${refundAmount.toLocaleString()} should be processed manually.`
        : 'Membership cancelled successfully';
      callbacks?.onSuccess?.(message);
    },
    onError: (error: unknown) => {
      callbacks?.onError?.(extractErrorMessage(error, 'Failed to cancel membership'));
    },
  });

  return { addManualMember, sendReminder, renewMembership, cancelMembership };
}
